import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useEventSource } from './useEventSource';
import {
  initialStreamState,
  type StreamAction,
  type StreamState,
} from './streamReducer';

/**
 * Context wrapper around the single `/api/stream` subscription.
 *
 * `StreamStateProvider` is exported alongside it so a test (or a screen shown
 * before authentication) can supply a fixed state without opening a socket —
 * the components below never learn which one they got.
 */

export interface StreamContextValue {
  readonly state: StreamState;
  readonly dispatch: React.Dispatch<StreamAction>;
  readonly reconnect: () => void;
}

const noop = (): void => {};

export const StreamContext = createContext<StreamContextValue>({
  state: initialStreamState,
  dispatch: noop,
  reconnect: noop,
});

export function StreamProvider({
  enabled,
  children,
}: {
  enabled: boolean;
  children: ReactNode;
}): JSX.Element {
  const { state, dispatch, reconnect } = useEventSource(enabled);
  const value = useMemo<StreamContextValue>(
    () => ({ state, dispatch, reconnect }),
    [state, dispatch, reconnect],
  );
  return <StreamContext.Provider value={value}>{children}</StreamContext.Provider>;
}

export function StreamStateProvider({
  value,
  children,
}: {
  value: StreamContextValue;
  children: ReactNode;
}): JSX.Element {
  return <StreamContext.Provider value={value}>{children}</StreamContext.Provider>;
}

export function useStream(): StreamContextValue {
  return useContext(StreamContext);
}

export function useStreamState(): StreamState {
  return useContext(StreamContext).state;
}
